// Avatar — the person mark (initials circle, photo when available), per
// COMPONENTS §Catalog. Presentational + trivial → hand-rolled (ADR-028).
//
// - Initials derive from the name (first letters of the first two words);
//   photo (`src`) replaces them when provided. Brand-tinted like Tag's brand
//   variant — one calm style, no per-person colour hashing (rainbow rows fight
//   the calm-list doctrine and per-project brand theming).
// - DECORATIVE by contract: aria-hidden always. The accessible name must come
//   from adjacent text (the name cell in a row, the option label) or the
//   wrapping control (IconButton aria-label) — same posture as Logo's
//   decorative variants. Never rely on the Avatar itself to announce a person.

import { useState } from 'react';

export type AvatarSize = 'sm' | 'md' | 'lg';

export interface AvatarProps {
  /** The person's display name — drives the initials. */
  name: string;
  /** Photo URL; replaces initials when given. */
  src?: string;
  /** sm 24px (dense rows) · md 32px (default) · lg 40px (profile headers). */
  size?: AvatarSize;
  className?: string;
}

const sizeClass: Record<AvatarSize, string> = {
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
};

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = words[0]?.charAt(0) ?? '';
  const second = words.length > 1 ? (words[words.length - 1]?.charAt(0) ?? '') : '';
  return (first + second).toLocaleUpperCase();
}

export function Avatar({ name, src, size = 'md', className = '' }: AvatarProps) {
  // A 404'd photo falls back to initials instead of a broken-image glyph.
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <span
      aria-hidden="true"
      className={
        `inline-flex items-center justify-center shrink-0 rounded-full overflow-hidden select-none font-medium ` +
        `bg-[var(--color-primary-light)] text-[var(--color-primary-display)] ${sizeClass[size]} ${className}`
      }
    >
      {src && !imgFailed ? (
        <img src={src} alt="" className="w-full h-full object-cover" onError={() => setImgFailed(true)} />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
}
